// src/js/utils/mcIdentity.ts
// Quick-task follow-up to Phase 253 — the shared avatar/name/badge DOM builder for the Today and
// Roster live-poll patchers (mc.ts's updateToday(), mc/roster.ts's row builder). Reproduces
// detail.latte's Today/Roster avatar+identity markup exactly: `createElement` + property
// assignment throughout, never `innerHTML` — `display_name`/`uuid` are wire data (Mojang name /
// player-supplied), and this file follows the same DOM-XSS discipline `mc.ts`'s
// `updateDetailPlayerList()` already established for the Players section (T-231-07/239 D-13/D-04).
//
// NOTE the avatar markup here differs from `updateDetailPlayerList()`'s own: Today/Roster's SSR
// avatar has no `rounded` class and hides itself via `style.visibility = 'hidden'` on a 404, not
// `style.display = 'none'` (detail.latte:505-511/586-592) — this file matches THAT markup, not the
// Players-section one, on purpose.

export interface McIdentityRow {
    uuid: string;
    display_name: string;
    unresolved: boolean;
    is_online_mode: boolean;
    linkable: boolean;
}

/** `<span class="mc-avatar-tile"><img class="mc-player-avatar" ...></span>`, matching
 *  detail.latte's Today/Roster avatar markup byte-for-byte. */
export function buildMcAvatarTile(uuid: string): HTMLElement {
    const tile = document.createElement('span');
    tile.className = 'mc-avatar-tile';

    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(uuid)}/32`;
    img.loading = 'lazy';
    img.width = 32;
    img.height = 32;
    img.alt = '';
    img.className = 'mc-player-avatar';
    img.addEventListener('error', () => {
        img.style.visibility = 'hidden';
    });

    tile.appendChild(img);
    return tile;
}

/**
 * The name span (possibly link-wrapped) plus the Resolving…/Non-Mojang identity badges, matching
 * detail.latte's Today/Roster identity block exactly. `serverKey` scopes the profile link the same
 * way `updateDetailPlayerList()`'s own link does (`/mc/players/{uuid}?server={key}`) — D-14's
 * absent-not-dead-link contract: `row.linkable` fails closed to `false`.
 */
export function buildMcNameWrap(row: McIdentityRow, serverKey: string): HTMLElement {
    const wrap = document.createElement('span');

    const nameSpan = document.createElement('span');
    nameSpan.textContent = row.display_name;
    nameSpan.className = row.unresolved ? 'mc-board-name mc-board-name--unresolved' : 'mc-board-name';

    if (row.linkable) {
        const link = document.createElement('a');
        link.href = `/mc/players/${encodeURIComponent(row.uuid)}?server=${encodeURIComponent(serverKey)}`;
        link.title = 'View profile';
        link.appendChild(nameSpan);
        wrap.appendChild(link);
    } else {
        wrap.appendChild(nameSpan);
    }

    if (row.unresolved) {
        const resolvingBadge = document.createElement('span');
        resolvingBadge.className = 'mc-identity-badge mc-identity-badge--resolving';
        resolvingBadge.title = "This player's current Minecraft name hasn't been resolved yet. The "
            + 'placeholder is built from their UUID so it can never be mistaken for a real name.';
        resolvingBadge.textContent = 'Resolving…';
        wrap.appendChild(resolvingBadge);
    }

    if (!row.is_online_mode) {
        const offlineBadge = document.createElement('span');
        offlineBadge.className = 'mc-identity-badge mc-identity-badge--offline';
        offlineBadge.title = "This UUID wasn't issued by Mojang (likely an offline-mode/cracked "
            + 'account). Ranked normally — never merged with or split from a real identity.';
        offlineBadge.textContent = 'Non-Mojang ID';
        wrap.appendChild(offlineBadge);
    }

    return wrap;
}
